/* A role-id list picker for the PDP forms (designs/pdp-catalog.md UI, PC6):
   chips with per-chip remove buttons and one `<input list="pdp-roles">` over
   the current roles. On Enter or blur a typed name that IS a role becomes a
   chip; one that is not raises the reason UNDER this picker and keeps the
   typed text — never the form-level error (UI/UX review, must #2). The
   datalist `#pdp-roles` is rendered once by the view. */
import { useState, type JSX } from "react";
import { normalizeName, type Role } from "../../../../shared/roles";

export const ROLE_LIST_ID = "pdp-roles";

/* a typed name resolves by normalized name or by id, over EVERY role — a
   retired role recorded on a row must still render and be removable, it is
   just not offered in the datalist */
export const resolveRole = (roles: Role[], typed: string): Role | undefined => {
  const wanted = normalizeName(typed);
  return wanted ? roles.find((r) => normalizeName(r.name) === wanted || r.id === wanted) : undefined;
};

export const roleChipReason = (typed: string): string =>
  `“${typed.trim()}” is not in the Roles catalog — add it on Roles first.`;

/* a retired role stays where it is recorded (its chip renders and can be
   removed) but is not offered for a new assignment (PC6) */
export const retiredChipReason = (name: string): string =>
  `\u201c${name}\u201d is retired — bring it back on Roles first.`;

export function RoleChips({
  id,
  label,
  value,
  roles,
  onChange,
}: {
  id: string;
  label: string;
  value: string[];
  roles: Role[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState<string | null>(null);
  const nameOf = (roleId: string): string => roles.find((r) => r.id === roleId)?.name ?? roleId;

  const take = (): void => {
    if (!typed.trim()) return;
    const hit = resolveRole(roles, typed);
    if (!hit) return setReason(roleChipReason(typed));
    if (hit.retired && !value.includes(hit.id)) return setReason(retiredChipReason(hit.name));
    if (!value.includes(hit.id)) onChange([...value, hit.id]);
    setTyped("");
    setReason(null);
  };

  return (
    <div className="field role-chips" id={id}>
      <span className="field-label">{label}</span>
      <ul className="role-chip-list" aria-label={label}>
        {value.map((roleId) => (
          <li className="role-chip" key={roleId}>
            {nameOf(roleId)}
            <button
              type="button"
              aria-label={`Remove ${nameOf(roleId)}`}
              onClick={() => onChange(value.filter((x) => x !== roleId))}
            >
              ×
            </button>
          </li>
        ))}
        <li className="role-chip-input">
          <input
            type="text"
            list={ROLE_LIST_ID}
            value={typed}
            placeholder="Type a role, Enter to add"
            aria-label={`Add a ${label.toLowerCase()} role`}
            onChange={(e) => {
              setTyped(e.target.value);
              setReason(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                take();
              }
            }}
            onBlur={take}
          />
        </li>
      </ul>
      {reason ? <div className="slot-note role-chip-reason">{reason}</div> : null}
    </div>
  );
}
